import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useReactToPrint } from 'react-to-print';
import { useQuery } from '@tanstack/react-query';

// MUI
import { DownloadRounded, UndoRounded } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// common
import MuiDialog from 'common/components/MuiDialog';
import MuiLoading from 'common/components/MuiLoading';
import NotFoundBox from 'common/components/NotFoundBox';
import SchemaForm from 'common/SchemaForm';
import useService from 'common/hook/useService';
import useSchoolYearMonthFilter from 'common/hook/useSchoolYearMonthFilter';
import { buildResourceSearchStr } from 'common/utils/schoolYearMonths';

// screen
import {
  NutritionMenuFilterModel,
  NutritionMenuModel,
  NutritionMenuStatusEnum,
} from 'modules/map/components/nutrition-menu/types';
import { A4_PRINT_SIZE, formatUTC } from 'modules/map/components/pdf-print-form/utils';
import { ClientModel } from 'modules/map/types';
import MenuTable from 'modules/map/components/nutrition-menu/MenuTable';
import PdfNutritionMenu from 'modules/map/components/nutrition-menu/pdf';

interface Props {
  clientData: ClientModel;
  open: boolean;
  onClose: () => void;
}

const NutritionMenuDialog = (props: Props) => {
  const { open, onClose, clientData } = props;
  const intl = useIntl();
  const contentToPrint = useRef(null);
  const service = useService();

  // Tháng/tuần do khung thực đơn của năm học quyết định — hook tự chọn khung mới nhất khi tải xong.
  const [filter, setFilter] = useState<NutritionMenuFilterModel>({
    state: NutritionMenuStatusEnum.propose,
    week: 1,
  });
  const { monthOptions, weekOptions, framesFailed } = useSchoolYearMonthFilter(filter, setFilter);

  // Năm lấy từ tháng đã chọn, không phải dayjs().year(): học kỳ 2 nằm ở năm dương lịch sau.
  // Thiếu tuần thì KHÔNG dựng searchStr: bỏ trống một đoạn sẽ làm `day` trượt vào chỗ của `week`.
  const searchStr = filter.week
    ? buildResourceSearchStr({
        monthKey: filter.month,
        week: filter.week,
        day: 0,
        state: filter.state,
      })
    : null;

  const { data, isFetching } = useQuery({
    queryKey: [filter, 'nutrition-menu'],
    queryFn: async () => {
      if (!service || !searchStr) return null;
      const json = await service.ClientNutritionMenuController_list({
        params: {
          orgId: clientData.operationCode,
          mode: 'real',
        },
        queries: {
          limit: 50,
          offset: 0,
          searchStr,
        },
      });
      return (json?.[0] as unknown as NutritionMenuModel) ?? null;
    },
    enabled: open && !!service && !!searchStr,
  });

  const days = data?.structuredData.data.items || [];
  const fromDate = formatUTC(days[0]?.datetime || '', 'L');
  const lastItemIndex = days.length - 1;
  const toDate = formatUTC(days[lastItemIndex]?.datetime || '', 'L');

  const handlePrint = useReactToPrint({
    contentRef: contentToPrint,
    pageStyle: A4_PRINT_SIZE,
  });

  const renderTable = ({
    isPrintedTable = false,
    refProp = null,
  }: {
    isPrintedTable?: boolean;
    refProp?: React.Ref<HTMLDivElement>;
  }) => {
    const renderTableContent = () => {
      if (isFetching) return <MuiLoading />;
      if (!data?.structuredData?.metadata?.isUsed) return <NotFoundBox />;
      if (!isPrintedTable) return <MenuTable data={data} filter={filter} />;

      return <PdfNutritionMenu data={data} customerType={clientData.type} />;
    };

    return (
      <Box
        sx={{
          overflow: isPrintedTable ? 'hidden' : undefined,
          height: isPrintedTable ? 0 : '100%',
        }}
      >
        <Box ref={refProp} className="flex justify-center w-full h-full">
          {renderTableContent()}
        </Box>
      </Box>
    );
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      title={'map.nutritionMenu'}
      maxWidth="desktop"
      fullWidth
      titleAlign="center"
      PaperProps={{
        sx: {
          margin: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
      disablePortal
    >
      <Box className="p-2 !pb-0 tablet:p-4">
        <SchemaForm<NutritionMenuFilterModel>
          formData={filter}
          onChange={setFilter}
          globalProps={{ noHelperText: true }}
          schema={{
            fields: ({ formProps: { isMobile } }) => {
              return {
                blank2: {
                  mode: 'node',
                  hidden: !isMobile,
                  render: () => {
                    return (
                      <Box className="flex items-center justify-center h-full">
                        <Typography variant="subtitle1">
                          {[fromDate, toDate].join(' - ')}
                        </Typography>
                      </Box>
                    );
                  },
                  propsWrapper: { mobile: 12 },
                },
                monthText: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Typography mt={1}>
                        <FormattedMessage id="map.month" />
                      </Typography>
                    );
                  },
                  propsWrapper: { mobile: undefined },
                },
                month: {
                  mode: 'select',
                  label: isMobile && <FormattedMessage id="map.month" />,
                  options: monthOptions,
                  rawOptions: true,
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: {
                    mobile: 6,
                    tablet: 2,
                    laptop: undefined,
                  },
                },
                weekText: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Box>
                        <Typography mt={1}>
                          <FormattedMessage id="map.week" />
                        </Typography>
                        {weekOptions.length === 0 && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            {intl.formatMessage({
                              id: framesFailed ? 'map.weekLoadFailed' : 'map.noWeek',
                            })}
                          </Typography>
                        )}
                      </Box>
                    );
                  },
                  propsWrapper: { mobile: undefined },
                },
                // Tuần phụ thuộc tháng nên đứng SAU tháng và khoá lại khi tháng chưa chốt khung.
                week: {
                  mode: 'select',
                  label: isMobile && <FormattedMessage id="map.week" />,
                  options: weekOptions.map((w) => ({ id: w.id, name: w.id })),
                  disabled: weekOptions.length === 0,
                  rawOptions: true,
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: { mobile: 6, tablet: 2 },
                },

                blank: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Box className="flex items-center justify-center h-full">
                        <Typography>{data && [fromDate, toDate].join(' - ')}</Typography>
                      </Box>
                    );
                  },
                  propsWrapper: { mobile: true },
                },
                statusText: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Typography mt={1}>
                        <FormattedMessage id="map.process" />
                      </Typography>
                    );
                  },
                  propsWrapper: { mobile: undefined },
                },
                state: {
                  label: isMobile && <FormattedMessage id="map.process" />,
                  mode: 'select',
                  options: [
                    {
                      id: NutritionMenuStatusEnum.propose,
                      name: intl.formatMessage({
                        id: 'map.propose',
                      }),
                    },
                    {
                      id: NutritionMenuStatusEnum.confirm,
                      name: intl.formatMessage({
                        id: 'confirm',
                      }),
                    },
                    {
                      id: NutritionMenuStatusEnum.complete,
                      name: intl.formatMessage({
                        id: 'map.completed',
                      }),
                    },
                  ],
                  rawOptions: true,
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: {
                    mobile: 6,
                    tablet: 2,
                    laptop: undefined,
                  },
                },
              };
            },
          }}
        />
      </Box>
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {renderTable({ isPrintedTable: true, refProp: contentToPrint })}
        {renderTable({})}
      </Box>
      <Box className="p-2 tablet:p-4 flex justify-end gap-2 tablet:gap-4">
        <Button
          startIcon={<DownloadRounded />}
          onClick={() => {
            handlePrint();
          }}
          disabled={!data || data.structuredData.data.items.every((day) => day.dishes.length === 0)}
        >
          <FormattedMessage id="map.download" />
        </Button>
        <Button variant="outlined" startIcon={<UndoRounded />} onClick={onClose}>
          <FormattedMessage id="back" />
        </Button>
      </Box>
    </MuiDialog>
  );
};

export default NutritionMenuDialog;
